import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api, { onAuthExpired, resetAuthExpired } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  // Track if we've already redirected to prevent storms
  const hasRedirected = useRef(false);

  const verifyAuth = useCallback(async () => {
    const token = api.getToken();
    const storedUser = api.getStoredUser();
    
    if (!token) {
      setUser(null);
      setIsAdmin(false);
      setIsLoading(false);
      return false;
    }

    try {
      const profile = await api.getProfile();
      setUser(profile);
      setIsAdmin(profile.is_admin === true);
      setAuthError(null);
      return true;
    } catch (error) {
      console.error('Auth verification failed:', error);
      // Don't clear token here - let the API handle 401s
      setUser(null);
      setIsAdmin(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Subscribe to auth expiration events
  useEffect(() => {
    const unsubscribe = onAuthExpired(() => {
      setUser(null);
      setIsAdmin(false);
      setAuthError('Session expired. Please login again.');
    });
    
    return unsubscribe;
  }, []);

  // Initial auth check
  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  const login = async (username, password) => {
    setAuthError(null);
    hasRedirected.current = false;
    resetAuthExpired();
    
    try {
      const data = await api.login(username, password);
      
      // Check if 2FA is required
      if (data.requires_2fa) {
        return { requires_2fa: true, temp_token: data.temp_token };
      }
      
      // After login, verify the profile to confirm admin status
      const profile = await api.getProfile();
      
      if (!profile.is_admin) {
        api.clearToken();
        throw new Error('Access denied. Admin privileges required.');
      }
      
      setUser(profile);
      setIsAdmin(true);
      return profile;
    } catch (error) {
      setAuthError(error.message);
      throw error;
    }
  };

  const verify2FA = async (tempToken, code) => {
    setAuthError(null);
    
    try {
      const data = await api.verify2FA(tempToken, code);
      
      // After 2FA verification, verify the profile
      const profile = await api.getProfile();
      
      if (!profile.is_admin) {
        api.clearToken();
        throw new Error('Access denied. Admin privileges required.');
      }
      
      setUser(profile);
      setIsAdmin(true);
      return profile;
    } catch (error) {
      setAuthError(error.message);
      throw error;
    }
  };

  const logout = useCallback(() => {
    api.logout();
    setUser(null);
    setIsAdmin(false);
    setAuthError(null);
    hasRedirected.current = false;
    resetAuthExpired();
  }, []);

  const value = {
    user,
    isAdmin,
    isLoading,
    isAuthenticated: !!user && isAdmin,
    authError,
    login,
    verify2FA,
    logout,
    verifyAuth,
    clearError: () => setAuthError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
